import { EventEmitter } from 'events';
import { GameMasterApi } from '@/api/backendService';
import { CharacterInfoGameService } from '@/services/characterInfoService.js';
import ClientManagerService from '@/services/clientManagerService.js';
import type {
  GMAvailableCharacter,
  GMCharacterSwitchResponse,
  GMCharacterSwitchRequestRequest
} from '@/api/dx-backend';

/**
 * Service for GM character impersonation functionality.
 * Uses the authenticated API client from backendService.
 */
class GMCharacterSwitchService extends EventEmitter {
  private availableCharacters: GMAvailableCharacter[] = [];
  private isSwitching: boolean = false;

  constructor() {
    super();
  }

  /**
   * Fetch available characters for GM impersonation.
   * Filters by position (if provided) and GM ownership.
   *
   * @param positionId - Optional position ID to filter characters by specific location
   */
  async getAvailableCharacters(positionId?: string): Promise<GMAvailableCharacter[]> {
    try {
      console.debug(
        `[GMCharacterSwitchService] Fetching available characters (positionId=${positionId || 'none'})`
      );

      const response = await GameMasterApi.gamemasterCharacterSwitchAvailableCharactersList(
        positionId
      );
      this.availableCharacters = response.data;

      console.info(
        `[GMCharacterSwitchService] Fetched ${this.availableCharacters.length} available characters` +
        (positionId ? ` at position ${positionId}` : '')
      );

      this.emit('charactersLoaded', this.availableCharacters);

      return this.availableCharacters;
    } catch (error) {
      console.error('[GMCharacterSwitchService] Error fetching available characters:', error);
      this.emit('error', error);
      throw error;
    }
  }

  /**
   * Switch to a different character.
   * Updates backend and triggers full data refresh.
   */
  async switchCharacter(characterId: string): Promise<GMCharacterSwitchResponse> {
    if (this.isSwitching) {
      console.warn('[GMCharacterSwitchService] Switch already in progress');
      throw new Error('Character switch already in progress');
    }

    try {
      this.isSwitching = true;
      this.emit('switchStarted', characterId);

      console.debug(`[GMCharacterSwitchService] Switching to character ${characterId}`);

      const requestData: GMCharacterSwitchRequestRequest = {
        character_id: characterId
      };

      const response = await GameMasterApi.gamemasterCharacterSwitchSwitchCharacterCreate(requestData);

      // Cached snapshots belong to the previous identity — drop them so avatars
      // and character details are re-resolved for the impersonated character.
      CharacterInfoGameService.invalidateAll();
      ClientManagerService.clearClientInfoCache();

      console.info(
        `[GMCharacterSwitchService] Successfully switched to character ${response.data.character_name}`
      );

      this.emit('switchCompleted', response.data);

      return response.data;
    } catch (error) {
      console.error(`[GMCharacterSwitchService] Error switching character:`, error);
      this.emit('switchFailed', error);
      throw error;
    } finally {
      this.isSwitching = false;
    }
  }

  /**
   * Get cached available characters without API call.
   */
  getCachedCharacters(): GMAvailableCharacter[] {
    return this.availableCharacters;
  }

  /**
   * Check if a character switch is in progress.
   */
  isSwitchInProgress(): boolean {
    return this.isSwitching;
  }
}

// Export singleton instance
export default new GMCharacterSwitchService();

// Export types for use in components
export type { GMAvailableCharacter, GMCharacterSwitchResponse };
